/**
 * Carrousel en boucle continue « Complète ton équipement » (sections/product-complete-equipment.liquid).
 *
 * Demande de Gillian (19.10.2026) : au bout de la rangée, la flèche de droite doit CONTINUER vers la
 * droite et faire revenir les premiers produits, comme un tour complet — pas revenir en arrière vers la
 * gauche (ce que fait l'attribut `infinite` du diaporama du thème, qui saute au début).
 *
 * Principe : la rangée est copiée une fois avant et une fois après les produits (copies inertes, sans
 * identifiant, cachées des lecteurs d'écran). On démarre sur la copie du milieu (les originaux). Quand le
 * défilement s'arrête dans une copie de bord, on recale instantanément la position sur l'équivalent au
 * milieu : les copies sont identiques, le saut est invisible. Les flèches avancent d'une page (le nombre
 * de colonnes), le doigt fait défiler librement (scroll-snap). Recalage toujours par scrollTo
 * instantané, jamais par scrollBy (voir la note defilement-programme-ios).
 */
class PdlLoop extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-pdl-loop-track]');
    if (!this.track) return;
    this.originaux = Array.from(this.track.children);
    this.n = this.originaux.length;
    // nombre de cartes visibles : --pdl-loop-n de la rangée (2 sur mobile), sinon le réglage de la section
    this.colonnes = () =>
      Number(getComputedStyle(this.track).getPropertyValue('--pdl-loop-n')) ||
      Number(getComputedStyle(this).getPropertyValue('--pdl-loop-cols')) ||
      4;
    this.reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const prev = this.querySelector('[data-pdl-loop-prev]');
    const next = this.querySelector('[data-pdl-loop-next]');

    this.#mesurerImage();
    if (this.n <= this.colonnes()) {
      // tout tient à l'écran : pas de boucle, pas de flèches
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
      return;
    }

    this.#cloner();
    this.track.style.scrollBehavior = 'auto';
    this.#aller(this.n, false);

    prev?.addEventListener('click', () => this.#page(-1));
    next?.addEventListener('click', () => this.#page(1));

    let minuteur = null;
    const finDeDefilement = () => this.#recaler();
    if ('onscrollend' in window) {
      this.track.addEventListener('scrollend', finDeDefilement);
    } else {
      this.track.addEventListener('scroll', () => {
        clearTimeout(minuteur);
        minuteur = setTimeout(finDeDefilement, 140);
      }, { passive: true });
    }

    if ('ResizeObserver' in window) {
      let largeur = this.track.clientWidth;
      new ResizeObserver(() => {
        this.#mesurerImage();
        if (this.track.clientWidth === largeur) return;
        largeur = this.track.clientWidth;
        this.#aller(this.n + this.#indexOriginal(), false);
      }).observe(this.track);
    }
  }

  /** Hauteur de l'image de la première carte → --pdl-loop-img-h (les flèches mobiles se centrent dessus). */
  #mesurerImage() {
    const img = this.track.querySelector('.card-gallery, .product-media-container, img');
    const h = img ? img.getBoundingClientRect().height : 0;
    if (h > 0) this.style.setProperty('--pdl-loop-img-h', `${Math.round(h)}px`);
  }

  #cloner() {
    const copie = (ou) => {
      const frag = document.createDocumentFragment();
      for (const el of this.originaux) {
        const c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('inert', '');
        c.dataset.pdlLoopClone = '1';
        for (const n of c.querySelectorAll('[id]')) n.removeAttribute('id');
        frag.appendChild(c);
      }
      ou === 'avant' ? this.track.prepend(frag) : this.track.append(frag);
    };
    copie('avant');
    copie('apres');
    this.slides = Array.from(this.track.children);
  }

  get #gauche() {
    return this.track.scrollLeft;
  }

  #positionDe(i) {
    return this.slides[i].offsetLeft - this.slides[0].offsetLeft;
  }

  /** index (dans slides, 0..3n-1) de la vignette la plus proche du bord gauche */
  #indexCourant() {
    const x = this.#gauche;
    let best = 0;
    let dist = Infinity;
    this.slides.forEach((s, i) => {
      const d = Math.abs(this.#positionDe(i) - x);
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    return best;
  }

  #indexOriginal() {
    return ((this.#indexCourant() % this.n) + this.n) % this.n;
  }

  #aller(i, doux) {
    this.track.scrollTo({ left: this.#positionDe(i), behavior: doux && !this.reduit ? 'smooth' : 'auto' });
  }

  #page(sens) {
    let i = this.#indexCourant() + sens * this.colonnes();
    // on reste dans [0, 3n) : les copies de bord absorbent le dépassement, le recalage suit
    i = Math.max(0, Math.min(i, this.slides.length - 1));
    this.#aller(i, true);
  }

  /** Dans une copie de bord ? On revient sur l'équivalent au milieu, sans animation. */
  #recaler() {
    const i = this.#indexCourant();
    if (i >= this.n && i < 2 * this.n) return;
    const equivalent = this.n + (((i % this.n) + this.n) % this.n);
    const decalage = this.#gauche - this.#positionDe(i);
    this.track.scrollTo({ left: this.#positionDe(equivalent) + decalage, behavior: 'auto' });
  }
}

if (!customElements.get('pdl-loop')) customElements.define('pdl-loop', PdlLoop);
